import { Grid, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { VendorInvoiceContainer, VatContainer } from "./styles";
import VatCountries from "./VatCountries/VatCountries";

import { CustomDate } from "../../../../models/custom-date/custom-date";
import { VendorInvoiceDTO, VendorInvoiceRef } from "../../../../models/legal-entity-vendors/vendor-invoices";
import { TransactionDTO } from "../../../../models/transactions/transaction-dto";
import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";

import { CountriesApiService } from "../../../../services/countries/countries-api.service";

import TransactionContext from "../../transaction-context/transaction-context";
import { LinkExistingInovice } from "../IssuedClientInvoice/LinkExistingInovice/LinkExistingInovice";

const defaultVendorInvoice: VendorInvoiceRef = {
  id: uuidv4(),
  companyAccount: null,
  isDeleted: false,
  vendor: null,
  invoiceNumber: "",
  totalAmount: 0,
  comment: "",
  vatNumber: "",
  currency: null,
  issueDate: new Date(),
  dueDate: new Date(),
  vatCountry: null,
  vatBases: [],

  receivedFromVendorOn: null,
  sentToAccountingOn: null,
  paidOn: null,
};

interface Props {
  transaction: TransactionDTO;
  handleChange: (field: keyof TransactionDTO, value: TransactionDTO[keyof TransactionDTO]) => void;
  defaultTransaction?: TransactionDTO | null;
}

const ReceivedVendorInvoice = ({
  transaction: { vendorInvoice, vendor, amount, currency, comment, transactionDate },
  transaction,
  handleChange,
  defaultTransaction,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_transaction.transaction_screen_two.vendor_invoice"; // tRP - translation root path

  const context = useContext(TransactionContext);

  useEffect(() => {
    if (vendor || amount || currency || comment || transactionDate) {
      handleChange("vendorInvoice", {
        ...defaultVendorInvoice,
        id: uuidv4(),
        ...vendorInvoice,
        ...(context?.invoice ? context.invoice : {}),
        vendor,
        currency,
        totalAmount: amount,
        comment,
        issueDate: CustomDate.ToDate(transactionDate) as Date,
      });
    }
  }, [vendor, amount, currency, comment, transactionDate]);

  const handleChangeVendorInvoice = (
    field: keyof VendorInvoiceDTO,
    value: VendorInvoiceDTO[keyof VendorInvoiceDTO]
  ) => {
    handleChange("vendorInvoice", vendorInvoice ? { ...vendorInvoice, [field]: value } : null);
  };

  return (
    <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px" }}>
      <VendorInvoiceContainer>
        {/* <AddVendor buttonVariant="contained" buttonText="text" /> */}
        <TextField
          label={`${t(`${tRP}.invoice_number`)}`}
          variant="outlined"
          value={vendorInvoice?.invoiceNumber ?? ""}
          onChange={(e) => handleChangeVendorInvoice("invoiceNumber", e.target.value)}
          sx={{ maxWidth: "300px" }}
        />

        <DesktopDatePicker
          value={vendorInvoice?.dueDate}
          onChange={(newValue) => {
            handleChangeVendorInvoice("dueDate", newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{ maxWidth: "300px" }}
              label={`${t(`${tRP}.invoice_due_date`)}`}
            />
          )}
        />
        <VatContainer>
          <AutocompleteDropdown
            labelKey={t(`${tRP}.vat_country`)}
            field="vatCountry"
            serviceApi={new CountriesApiService()}
            handleChange={handleChangeVendorInvoice}
            propsItem={vendorInvoice}
            sxProps={{ maxWidth: "300px" }}
          />
          <VatCountries
            transaction={transaction}
            handleChange={handleChange}
          />
        </VatContainer>
      </VendorInvoiceContainer>
      {!context.invoice && !defaultTransaction?.vendorInvoice && (
        <LinkExistingInovice
          vendor={vendor}
          invoiceType={"vendor"}
          handleNotifyParent={(invoice: any) => {
            handleChange("vendor", invoice?.vendor);
            handleChange("vendorInvoice", invoice);
          }}
          item={transaction}
        />
      )}
    </Grid>
  );
};

export default ReceivedVendorInvoice;
